import type { ResponseModel } from "services/ApiClient/responseModel";
import { SimpleUser } from "features/User/models";
import { NewHireJourney } from "models/newHire";

// Event type prefixes
export const EVENT_TYPE_PREFIXES = [
  "email",
  "message",
  "task",
  "meeting",
  "new_hire_journey",
  "issue",
  "form",
  "nudge",
] as const;

export type EventTypePrefix = typeof EVENT_TYPE_PREFIXES[number];

// Full event names
export const EVENT_NAMES = [
  "email.sent",
  "email.failed",
  "email.skipped",
  "message.sent",
  "task.assigned",
  "task.assignee.changed",
  "task.completed",
  "task.due_date.changed",
  "task.notification.sent",
  "task.reminder.sent",
  "task.skipped",
  "meeting.scheduled",
  "meeting.cancelled",
  "meeting.completed",
  "meeting.declined",
  "meeting.no_response",
  "meeting.rescheduled",
  "new_hire_journey.created",
  "new_hire_journey.activated",
  "new_hire_journey.buddy.assigned",
  "new_hire_journey.buddy.changed",
  "new_hire_journey.manager.assigned",
  "new_hire_journey.manager.changed",
  "new_hire_journey.people_team_contact.assigned",
  "new_hire_journey.people_team_contact.changed",
  "new_hire_journey.start_date.changed",
  "form.completed",
  "issue.created",
  "issue.resolved",
  "nudge.sent",
] as const;

export type EventName = typeof EVENT_NAMES[number];

// Platform Event interfaces
export interface PlatformEventMeta {
  entityName?: string;
  previousStatus?: string;
  newStatus?: string;
  subject?: string;
  recipientCount?: number;
  recipientType?: string;
  recipientId?: string;
  assigneeId?: string;
  taskAction?: string;
  newHireId?: string;
  startDate?: string;
  pending?: boolean;
  previousAssigneeId?: string;
  newAssigneeId?: string;
  previousBuddyId?: string;
  newBuddyId?: string;
  previousManagerId?: string;
  newManagerId?: string;
  previousPeopleTeamContactId?: string;
  newPeopleTeamContactId?: string;
  previousStartDate?: string;
  newStartDate?: string;
  previousDueDate?: string;
  newDueDate?: string;
  notificationType?: string;
  interval?: string;
  [key: string]: unknown;
}

export interface PlatformEventMini {
  id: string;
  eventName: EventName;
  entityType: string | null;
  entityId: string | null;
  occurredAt: string;
  meta: PlatformEventMeta;
  createdAt: string;
}

export interface PlatformEvent extends PlatformEventMini {
  newHireJourneyId: string | null;
  actorId: string | null;
  companyId: string;
  actor: SimpleUser | null;
  newHire: SimpleUser | null;
}

export interface PlatformEventExtended extends PlatformEvent {
  newHireJourney: NewHireJourney | null;
}

// API response types
export type PlatformEventsResponse = ResponseModel<PlatformEvent[]>;

// Filter types
export interface PlatformEventFilters {
  eventType?: EventTypePrefix;
  eventName?: EventName;
  newHireJourneyId?: string;
  newHireId?: string;
  actorId?: string;
  entityType?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// Create event payload
export interface CreatePlatformEventPayload {
  eventName: string;
  entityType?: string;
  entityId?: string;
  newHireJourneyId?: string;
  occurredAt?: string;
  meta?: Record<string, unknown>;
}
